import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GoBack, PrimaryButton } from "../components/Buttons";
import { CollapsibleSection } from "../components/Cards";
import { SelectInput, TextInput, NumberInput, TextAreaInput, DateInput } from "../components/Inputs";
import { PageHeader } from "../components/Layouts";
import { useLoaderStore, useUserStore } from "../context";
import { useAlert, useApi } from "../hooks";
import constants from "../utils/constants";
import { convertToLabelValueList, convertToLabelValue, resolveSystemTenantId, isDev, formatCurrency, classNames } from "../utils/helpers";
import { ModalWrapper } from "../components/Modals";
import { Pill, InfoTooltip } from "../components/Displays";
import { XMarkIcon } from "@heroicons/react/24/solid";

const YEAR_IN_DAYS = 365;

export default function Product() {
  let { id } = useParams();
  const navigate = useNavigate();
  const alert = useAlert();
  const isCreatingNew = id === "0";

  const { fetch, post } = useApi();
  const { setShowLoader } = useLoaderStore();
  const { currentUser, activeSystemTenant } = useUserStore();

  const [product, setProduct] = useState({ id, pricingModel: constants.PRICING_MODEL.Flat.value });

  const [systemTenantIdData, setSystemTenantIdData] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [durationTypes, setDurationTypes] = useState([]);
  const [eligibleProducts, setEligibleProducts] = useState([]);
  const [selectedTrialOffer, setSelectedTrialOffer] = useState();
  const [selectedIndex, setSelectedIndex] = useState();
  // SPR-013: re-pricing existing subscriptions. A product price change does NOT move live subscriptions —
  // each pins a RenewalPrice snapshot the renewal prefers — so we prompt after a price change and also offer a
  // standalone re-run for products whose price was changed earlier. Declining GRANDFATHERS, deliberately.
  const [originalPrice, setOriginalPrice] = useState(null);
  const [repricePreview, setRepricePreview] = useState(null); // non-null => dialog open
  const [repricePending, setRepricePending] = useState(false);
  const [navigateAfterReprice, setNavigateAfterReprice] = useState(false);
  const IS_DEV = isDev();

  useEffect(() => {
    getSystemTenantsSimple();
    getProductTypes();
    getDurationTypes();
    getProduct();
  }, []);

  // Eligible cross-sell products: other products belonging to the same system
  // tenant as the product being edited. Refetch whenever that tenant changes.
  useEffect(() => {
    getEligibleProducts(product?.systemTenantId);
  }, [product?.systemTenantId]);

  function getProduct(){
    if (isCreatingNew) return;
    setShowLoader(true);
    fetch(`admin/productInfo/${id}/${resolveSystemTenantId(currentUser, activeSystemTenant)}`)
    .then((res) => {
      setProduct(res);
      // SPR-013: remember the price as loaded so we can tell on save whether it actually changed.
      setOriginalPrice(res?.price ?? null);
    })
    .catch(() => {
      alert("Server error", "Could not retrieve product", "error");
    })
    .finally(() => setShowLoader(false));
  }

  function getSystemTenantsSimple() {
    fetch("admin/getSystemTenantsSimple", { sortField: "Name", id: resolveSystemTenantId(currentUser, activeSystemTenant) })
    .then((res) => {
        setSystemTenantIdData(res);
    });
  }

  function getProductTypes() {
    fetch("Reference/GetProductTypes")
    .then(res => {
      setProductTypes(convertToLabelValueList(res));
    })
    .catch(err => {
      alert("Error", "Unable to fetch product types", "danger")
    })
  }

  function getEligibleProducts(systemTenantId) {
    if (!systemTenantId) {
      setEligibleProducts([]);
      return;
    }
    fetch("admin/getProductsSimple", { sortField: "Name", systemTenantId })
    .then(res => {
      setEligibleProducts(Array.isArray(res) ? res : []);
    })
    .catch(err => {
      alert("Error", "Unable to fetch products", "danger")
    })
  }

  function getDurationTypes() {
    fetch("Reference/GetDurationTypes")
    .then(res => {
      setDurationTypes(convertToLabelValueList(res));
    })
    .catch(err => {
      alert("Error", "Unable to fetch duration types", "danger")
    })
  }

  const handleChange = (property, value) => {
    setProduct({
      ...product,
      [property]: value,
    });
  };

  const handleOfferChange = (property, value) => {
    if (!selectedTrialOffer) return;
    setSelectedTrialOffer({
      ...selectedTrialOffer,
      [property]: value,
    });
  };

  // SPR-013: fetch the re-price preview. Returns null (and stays silent) on failure — this must never block the
  // save flow, since the product itself saved fine and the operator can still use the standalone action.
  const fetchRepricePreview = (productId) =>
    // Pass the PRODUCT'S OWN system tenant — the admin surface spans many, so omitting it (or assuming
    // Margot's) mis-scopes the lookup for any non-Margot product. Server derives it if absent.
    fetch(`admin/PreviewRepriceExistingSubscriptions/${productId}/${product?.systemTenantId ?? ""}`)
      .then((res) => res)
      .catch(() => null);

  const applyReprice = () => {
    const productId = repricePreview?.productId ?? product?.id;
    if (!productId) return;
    setRepricePending(true);
    post(`admin/RepriceExistingSubscriptions/${productId}/${product?.systemTenantId ?? ""}`)
      .then((res) => {
        alert(
          "Subscriptions updated",
          `${res?.updatedCount ?? 0} subscription(s) will now renew at ${formatCurrency(res?.currentProductPrice ?? 0, true)}.`,
          "success"
        );
        finishReprice();
      })
      .catch((err) => {
        alert("Unable to update subscriptions", err?.data?.message || "Please try again", "error");
        setRepricePending(false);
      });
  };

  // Declining is a first-class outcome (grandfather existing subscribers), NOT a cancel — so it closes the
  // dialog and continues the original flow rather than leaving the operator stuck.
  const finishReprice = () => {
    setRepricePending(false);
    setRepricePreview(null);
    if (navigateAfterReprice) {
      setNavigateAfterReprice(false);
      navigate("/products");
    }
  };

  const handleSave = () => {
    if (!isValid()) return;

    setShowLoader(true);
    post("admin/saveProduct", product)
      .then(async (res) => {
        alert("Success", "Saved product", "success");
        // SPR-013: if the price actually changed on an EXISTING product, live subscriptions are still pinned to
        // the old price. Surface that at the moment of intent instead of letting it pass silently.
        const savedId = res?.id ?? product?.id;
        const priceChanged = !isCreatingNew && originalPrice !== null && product?.price !== originalPrice;
        if (priceChanged && savedId) {
          const preview = await fetchRepricePreview(savedId);
          if (preview?.affectedCount > 0) {
            setOriginalPrice(product?.price ?? null); // don't re-prompt for the same change
            setNavigateAfterReprice(true);
            setRepricePreview(preview);
            return; // hold navigation until the operator chooses
          }
        }
        navigate("/products");
      })
      .catch((err) => {
        alert(
          "Unable to save product",
          err.data.message || "Please try again",
          "error"
        );
        console.error(err);
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const isValid = () => {
    if (!product.systemTenantId) {
      alert("Form invalid", "System Tenant Id must have a value", "warning");
      return false;
    }
    if (Number.isNaN(product.price) || product.price < 0) {
      alert("Form invalid", "Price must have a value", "warning");
      return false;
    }
    if (!product.name || !product.name.trim()) {
      alert("Form invalid", "Name must have a value", "warning");
      return false;
    }
    if (!product.productType) {
      alert("Form invalid", "Product Type Id must have a value", "warning");
      return false;
    }
    // add validations here

    return true;
  };

  const isPercentageOffer = (offer) => offer?.discountType === constants.DISCOUNT_TYPE.Percentage.value;

  const isOfferOfType = (offer, offerType) => offer?.offerType === offerType.value;

  // EVO-003 / SPR-011 T-003: the application-mode control applies only to the discount-bearing offer types
  // (Entry, Retention, Seasonal, CrossSell — NOT Custom). Custom offers are always SingleTerm.
  const isDiscountBearingOffer = (offer) =>
    isOfferOfType(offer, constants.OFFER_TYPE.Entry) ||
    isOfferOfType(offer, constants.OFFER_TYPE.Retention) ||
    isOfferOfType(offer, constants.OFFER_TYPE.Seasonal) ||
    isOfferOfType(offer, constants.OFFER_TYPE.CrossSell);

  const isRecurringOffer = (offer) => offer?.discountMode === constants.DISCOUNT_MODE.RecurringPerPeriod.value;

  function createNewOffer() {
    return ({
      id: 0,
      productId: product?.id,
      name: "",
      price: 0,
      durationCount: 7,
      durationType: constants.DURATION_TYPE.Days.value,
      daysSinceLastOffer: YEAR_IN_DAYS,
      discountType: constants.DISCOUNT_TYPE.Fixed.value,
      discountPercent: null,
      // EVO-003 / SPR-011 T-003: discount application mode. SingleTerm (default) = today's behavior.
      discountMode: constants.DISCOUNT_MODE.SingleTerm.value,
      recurringPeriods: null,
      offerType: constants.OFFER_TYPE.Custom.value,
      campaignStartsAt: null,
      campaignEndsAt: null,
      qualifyingProductId: null,
      minSeatQuantity: null,
      minTenureDays: null,
    });
  }

  function closeModal() {
    setSelectedTrialOffer(undefined);
    setSelectedIndex(undefined);
  }

  const isOfferValid = (offer) => {
    if (!offer?.name || !offer.name.trim()) {
      alert("Offer invalid", "Name must have a value", "warning");
      return false;
    }
    if (isPercentageOffer(offer)) {
      if (Number.isNaN(offer.discountPercent) || offer.discountPercent === null || offer.discountPercent <= 0 || offer.discountPercent > 100) {
        alert("Offer invalid", "Discount percent must be between 0 and 100", "warning");
        return false;
      }
    } else if (Number.isNaN(offer.price) || offer.price < 0) {
      alert("Offer invalid", "Price must have a value", "warning");
      return false;
    }
    // EVO-003 / SPR-011 T-003: RecurringPerPeriod requires N >= 2 (N=1 is equivalent to SingleTerm — block it to
    // avoid a pointless "recurring" of one period). SingleTerm ignores recurringPeriods. Only enforced for the
    // discount-bearing types that expose the application-mode control.
    if (isDiscountBearingOffer(offer) && isRecurringOffer(offer)) {
      if (Number.isNaN(offer.recurringPeriods) || offer.recurringPeriods === null || offer.recurringPeriods < 2) {
        alert("Offer invalid", "Recurring offers require at least 2 periods (use Single term for a one-off discount)", "warning");
        return false;
      }
    }
    // Per-type requirements (server treats a misconfigured typed offer as ineligible).
    if (isOfferOfType(offer, constants.OFFER_TYPE.Seasonal)) {
      if (!offer.campaignStartsAt || !offer.campaignEndsAt) {
        alert("Offer invalid", "Seasonal offers require both a campaign start and end date", "warning");
        return false;
      }
      if (new Date(offer.campaignStartsAt) > new Date(offer.campaignEndsAt)) {
        alert("Offer invalid", "Campaign start date must be on or before the end date", "warning");
        return false;
      }
    } else if (isOfferOfType(offer, constants.OFFER_TYPE.CrossSell)) {
      if (!offer.qualifyingProductId) {
        alert("Offer invalid", "Cross-sell offers require a qualifying product", "warning");
        return false;
      }
    }
    // SPR-009 retired — SeatExpansion / Loyalty validation retained for reference:
    // } else if (isOfferOfType(offer, constants.OFFER_TYPE.SeatExpansion)) {
    //   if (Number.isNaN(offer.minSeatQuantity) || offer.minSeatQuantity === null || offer.minSeatQuantity < 1) {
    //     alert("Offer invalid", "Seat expansion offers require a minimum seat quantity of at least 1", "warning");
    //     return false;
    //   }
    // } else if (isOfferOfType(offer, constants.OFFER_TYPE.Loyalty)) {
    //   if (Number.isNaN(offer.minTenureDays) || offer.minTenureDays === null || offer.minTenureDays < 1) {
    //     alert("Offer invalid", "Loyalty offers require a minimum tenure of at least 1 day", "warning");
    //     return false;
    //   }
    return true;
  };

  const describeOfferDiscount = (offer) =>
    isPercentageOffer(offer)
      ? `${offer.discountPercent ?? 0}% off`
      : formatCurrency(offer.price, true);

  // Options for the cross-sell qualifying-product dropdown: same-tenant products,
  // excluding the product currently being edited. {value: productId, label: name}.
  const qualifyingProductOptions = convertToLabelValueList(
    eligibleProducts.filter((p) => String(p.id) !== String(product?.id))
  );

  return (
    <div>
      {/* Header */}
      <PageHeader
        title={
          isCreatingNew || !product ? "Create new product" : `Manage product ${IS_DEV ? product.id : ""}`
        }
      />
      <GoBack display="Manage all products" navigateTo="/products" />

      <div className="bg-gray-100 shadow-md rounded-md shadow-primaryColor/25">
        {/* Form */}
        <CollapsibleSection title="Info">
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-4">
            <div>
              <SelectInput
                label="System Tenant Id"
                options={convertToLabelValueList(systemTenantIdData)}
                onClear={() => handleChange("systemTenantId", undefined)}
                value={convertToLabelValue(systemTenantIdData?.find(x => x.id === product?.systemTenantId))}
                onChange={(systemTenantId) => handleChange("systemTenantId", systemTenantId.value)}
                isSearchable
              />
            </div>
            <div>
              <TextInput
                label="Name"
                value={product?.name ?? ""}
                onChange={(e) => handleChange("name", e.target.value)}
                required
                maxLength={250}
              />
            </div>
            <div>
              <NumberInput
                label="Price in pennies"
                value={product?.price ?? ""}
                onChange={(e) => handleChange("price", e.target.valueAsNumber)}
                required
                isCurrency
                isPennies
              />
            </div>
            <div>
              <SelectInput
                label="Duration Type"
                options={durationTypes}
                onClear={() => handleChange("durationType", undefined)}
                value={durationTypes.find(x => x.value === product?.durationType)}
                onChange={(e) => handleChange("durationType", e.value)}
                isSearchable
              />
            </div>
            <div>
              <NumberInput
                label="Duration Amount"
                value={product?.durationCount ?? ""}
                onChange={(e) => handleChange("durationCount", e.target.valueAsNumber)}
                min={0}
              />
            </div>
            <div>
              <SelectInput
                label="Product Type"
                options={productTypes}
                onClear={() => handleChange("productType", undefined)}
                value={productTypes.find(x => x.value === product?.productType)}
                onChange={(e) => handleChange("productType", e.value)}
                isSearchable
              />
            </div>
            <div>
              <SelectInput
                label="Pricing Model"
                options={constants.PRICING_MODELS}
                value={constants.PRICING_MODELS.find(x => x.value === product?.pricingModel) ?? constants.PRICING_MODEL.Flat}
                onChange={(e) => handleChange("pricingModel", e.value)}
              />
            </div>
            {product?.productType === constants.PRODUCT_TYPE.SUPPLEMENT.value &&
              (<div>
                <NumberInput
                  label="Supplemental Feature Qty"
                  value={product?.supplementalFeatureQty ?? ""}
                  onChange={(e) => handleChange("supplementalFeatureQty", e.target.valueAsNumber)}
                  min={0}
                />
              </div>)}
            <div>
              <TextAreaInput
                label="Tag"
                value={product?.tag ?? ""}
                onChange={(e) => handleChange("tag", e.target.value)}
                maxLength={1000}
              />
            </div>

            {/* Trial Offers */}
            <div className="flex flex-col col-span-full">
              <hr/>
              <div className="flex flex-row flex-wrap justify-between my-4">
                <span className="content-center"><h2>Trial Offers</h2></span>
                <span>
                  <div className="hover:cursor-pointer"
                    onClick={() => {
                      setSelectedIndex(undefined);
                      setSelectedTrialOffer(createNewOffer());
                    }}
                  >
                    <Pill title={"Add trial offer"}/>
                  </div>
                </span>
              </div>
              {Array.isArray(product?.trialOffers) && product.trialOffers.map((r, idx) => (
                <div
                  key={`product-offer-${idx}`}
                  className="px-2 py-4 flex flex-row hover:cursor-pointer hover:text-slate-500 hover:bg-gray-200"
                  onClick={() => {
                    setSelectedTrialOffer(r);
                    setSelectedIndex(idx);
                  }}
                >
                  <div className="flex flex-1">{r.name}</div>
                  <div className="flex flex-1">{constants.DISCOUNT_TYPES.find(x => x.value === r?.discountType)?.label ?? "Fixed"}</div>
                  <div className="flex flex-1">{describeOfferDiscount(r)}</div>
                  <div className="flex flex-1">{r.durationCount} {constants.DURATION_TYPES_OPT_PLURALITY.find(x => x.value === r?.durationType)?.label}</div>
                  <div>
                    <XMarkIcon
                      title="Remove"
                      className="w-6 text-white bg-delete rounded-md p-1 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        let newOffers = product?.trialOffers?.filter((x, i) => (i !== idx));
                        setProduct({...product, trialOffers: newOffers});
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleSection>

        {/* SPR-013: standalone re-price action. Needed in addition to the save-time prompt because a product whose
            price was changed EARLIER (before this feature, or by someone who declined) can otherwise never be
            resynced without a pointless re-save. Existing subscriptions stay on their old price until this is run. */}
        {!isCreatingNew && (
          <div className="mt-8 border-t pt-6">
            <div className="text-sm text-gray-700">
              Changing this product's price does <span className="font-semibold">not</span> change what existing
              subscribers are charged — they keep renewing at the price captured when they subscribed. Use this to
              move them to the current price ({formatCurrency(product?.price ?? 0, true)}), or leave it alone to
              keep their existing pricing.
            </div>
            <button
              type="button"
              className="mt-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              onClick={() => {
                setShowLoader(true);
                fetchRepricePreview(product.id)
                  .then((preview) => {
                    if (!preview) {
                      alert("Server error", "Could not check existing subscriptions", "error");
                      return;
                    }
                    if (!preview.affectedCount) {
                      alert(
                        "Nothing to update",
                        `All ${preview.totalActiveRenewals ?? 0} active subscription(s) already renew at ${formatCurrency(preview.currentProductPrice ?? 0, true)}.`,
                        "info"
                      );
                      return;
                    }
                    setNavigateAfterReprice(false);
                    setRepricePreview(preview);
                  })
                  .finally(() => setShowLoader(false));
              }}
            >
              Apply current price to existing subscriptions…
            </button>
          </div>
        )}

        {/* Submit button */}
        <div className={classNames("mt-12", constants.SAVE_BUTTON_CONTAINER_CLASS.default)}>
          <PrimaryButton
            text={isCreatingNew ? "Create" : "Save"}
            onClick={handleSave}
          />
        </div>
      </div>

      {/* SPR-013: confirm dialog. "Keep existing pricing" is a legitimate outcome (grandfathering), not a cancel. */}
      <ModalWrapper open={!!repricePreview} setOpen={() => finishReprice()}>
        <div className="w-[85vw] lg:w-[28vw] flex flex-col gap-y-4">
          <div className="text-base font-semibold text-gray-900">Apply new price to existing subscriptions?</div>
          <div className="text-sm text-gray-700">
            <span className="font-semibold">{repricePreview?.affectedCount}</span> of{" "}
            {repricePreview?.totalActiveRenewals} active subscription(s) still renew at their original price
            {repricePreview?.exampleStalePrice != null && (
              <> (e.g. {formatCurrency(repricePreview.exampleStalePrice, true)})</>
            )}
            . Applying will make them renew at{" "}
            <span className="font-semibold">{formatCurrency(repricePreview?.currentProductPrice ?? 0, true)}</span>{" "}
            instead.
          </div>
          <div className="text-xs text-gray-500">
            Only future charges are affected — amounts already charged are never changed, and cancelled or ended
            subscriptions are left alone.
          </div>
          {repricePreview?.hasAlreadyChargedUpcomingPeriod && (
            <div className="rounded-md bg-yellow-50 p-3 text-xs text-yellow-800">
              Note: at least one of these subscriptions has <span className="font-semibold">already been charged</span>{" "}
              for its upcoming period. That period still bills at the old price; only the renewal after it changes.
            </div>
          )}
          {/* Equal-width actions: PrimaryButton is already w-full h-[38px], so an equal-column grid makes both
              buttons identical in width (and matching height) regardless of label length. */}
          <div className="mt-2 grid grid-cols-2 gap-x-3">
            <button
              type="button"
              disabled={repricePending}
              className="w-full h-[38px] inline-flex items-center justify-center rounded-md bg-white px-3 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
              onClick={finishReprice}
            >
              <span className="whitespace-nowrap">Keep existing pricing</span>
            </button>
            <PrimaryButton
              text={repricePending ? "Applying…" : "Apply new price"}
              onClick={applyReprice}
              disabled={repricePending}
            />
          </div>
        </div>
      </ModalWrapper>

      <ModalWrapper open={!!selectedTrialOffer} setOpen={closeModal}>
        <div className="w-[85vw] lg:w-[20vw] flex flex-col gap-y-4">
          <div>
            <TextInput
              label={"Name"}
              value={selectedTrialOffer?.name ?? ""}
              onChange={(e) => handleOfferChange("name", e.target.value)}
              required
              maxLength={250}
            />
          </div>
          <div>
            <SelectInput
              idParam={constants.EXP_IDS.OfferType}
              label="Offer Type"
              options={constants.OFFER_TYPES}
              value={constants.OFFER_TYPES.find(x => x.value === selectedTrialOffer?.offerType) ?? constants.OFFER_TYPE.Custom}
              onChange={(e) => handleOfferChange("offerType", e.value)}
              required
            />
            <InfoTooltip id={`#${constants.EXP_IDS.OfferType}`}>
              {constants.EXPLANATIONS.OfferType}
            </InfoTooltip>
          </div>
          <div>
            <SelectInput
              idParam={constants.EXP_IDS.OfferDiscountType}
              label="Discount Type"
              options={constants.DISCOUNT_TYPES}
              value={constants.DISCOUNT_TYPES.find(x => x.value === selectedTrialOffer?.discountType) ?? constants.DISCOUNT_TYPE.Fixed}
              onChange={(e) => handleOfferChange("discountType", e.value)}
              required
            />
            <InfoTooltip id={`#${constants.EXP_IDS.OfferDiscountType}`}>
              {constants.EXPLANATIONS.OfferDiscountType}
            </InfoTooltip>
          </div>
          {isPercentageOffer(selectedTrialOffer)
            ? (
              <div>
                <NumberInput
                  id={constants.EXP_IDS.OfferDiscountPercent}
                  label="Discount Percent (0-100)"
                  value={selectedTrialOffer?.discountPercent ?? ""}
                  onChange={(e) => handleOfferChange("discountPercent", e.target.valueAsNumber)}
                  min={0}
                  max={100}
                  required
                />
                <InfoTooltip id={`#${constants.EXP_IDS.OfferDiscountPercent}`}>
                  {constants.EXPLANATIONS.OfferDiscountPercent}
                </InfoTooltip>
              </div>
            )
            : (
              <div>
                <NumberInput
                  id={constants.EXP_IDS.OfferPrice}
                  label="Price in pennies"
                  value={selectedTrialOffer?.price ?? ""}
                  onChange={(e) => handleOfferChange("price", e.target.valueAsNumber)}
                  required
                  isCurrency
                  isPennies
                />
                <InfoTooltip id={`#${constants.EXP_IDS.OfferPrice}`}>
                  {constants.EXPLANATIONS.OfferPrice}
                </InfoTooltip>
              </div>
            )}

          {/* EVO-003 / SPR-011 T-003: application-mode control — only for the discount-bearing types (Entry,
              Retention, Seasonal, CrossSell — not Custom). Follows the same per-type conditional pattern. */}
          {isDiscountBearingOffer(selectedTrialOffer) && (
            <div>
              <SelectInput
                idParam={constants.EXP_IDS.DiscountMode}
                label="Application Mode"
                options={constants.DISCOUNT_MODES}
                value={constants.DISCOUNT_MODES.find(x => x.value === selectedTrialOffer?.discountMode) ?? constants.DISCOUNT_MODE.SingleTerm}
                onChange={(e) => handleOfferChange("discountMode", e.value)}
                required
              />
              <InfoTooltip id={`#${constants.EXP_IDS.DiscountMode}`}>
                {constants.EXPLANATIONS.DiscountMode}
              </InfoTooltip>
            </div>
          )}

          {isDiscountBearingOffer(selectedTrialOffer) && isRecurringOffer(selectedTrialOffer) && (
            <div>
              <NumberInput
                id={constants.EXP_IDS.RecurringPeriods}
                label="Recurring Periods"
                value={selectedTrialOffer?.recurringPeriods ?? ""}
                onChange={(e) => handleOfferChange("recurringPeriods", Number.isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber)}
                min={2}
                required
              />
              <InfoTooltip id={`#${constants.EXP_IDS.RecurringPeriods}`}>
                {constants.EXPLANATIONS.RecurringPeriods}
              </InfoTooltip>
            </div>
          )}

          <div>
            <SelectInput
              idParam={constants.EXP_IDS.OfferDurationType}
              label="Duration Type"
              options={durationTypes}
              value={durationTypes.find(x => x.value === selectedTrialOffer?.durationType)}
              onChange={(e) => handleOfferChange("durationType", e.value)}
              required
              isSearchable
            />
            <InfoTooltip id={`#${constants.EXP_IDS.OfferDurationType}`}>
              {constants.EXPLANATIONS.OfferDurationType}
            </InfoTooltip>
          </div>
          <div>
            <NumberInput
              id={constants.EXP_IDS.OfferDurationAmount}
              label="Duration Amount"
              value={selectedTrialOffer?.durationCount ?? ""}
              onChange={(e) => handleOfferChange("durationCount", e.target.valueAsNumber)}
              min={0}
              required
            />
            <InfoTooltip id={`#${constants.EXP_IDS.OfferDurationAmount}`}>
              {constants.EXPLANATIONS.OfferDurationAmount}
            </InfoTooltip>
          </div>
          <div>
            <NumberInput
              id={constants.EXP_IDS.DaysSinceLastOffer}
              label={"Days Since Last Offer Threshold"}
              value={selectedTrialOffer?.daysSinceLastOffer ?? ""}
              onChange={(e) => handleOfferChange("daysSinceLastOffer", e.target.valueAsNumber)}
              min={0}
              required
            />
            <InfoTooltip id={`#${constants.EXP_IDS.DaysSinceLastOffer}`}>
              {constants.EXPLANATIONS.DaysSinceLastOffer}
            </InfoTooltip>
          </div>

          {/* Per-type behavioral fields (EVO-002): shown only for the selected offer type. */}
          {isOfferOfType(selectedTrialOffer, constants.OFFER_TYPE.Seasonal) && (
            <>
              <div id={constants.EXP_IDS.CampaignStartsAt}>
                <DateInput
                  label="Campaign Starts At"
                  value={selectedTrialOffer?.campaignStartsAt ?? ""}
                  onChange={(e) => handleOfferChange("campaignStartsAt", e.target.value || null)}
                  required
                />
                <InfoTooltip id={`#${constants.EXP_IDS.CampaignStartsAt}`}>
                  {constants.EXPLANATIONS.CampaignStartsAt}
                </InfoTooltip>
              </div>
              <div id={constants.EXP_IDS.CampaignEndsAt}>
                <DateInput
                  label="Campaign Ends At"
                  value={selectedTrialOffer?.campaignEndsAt ?? ""}
                  onChange={(e) => handleOfferChange("campaignEndsAt", e.target.value || null)}
                  required
                />
                <InfoTooltip id={`#${constants.EXP_IDS.CampaignEndsAt}`}>
                  {constants.EXPLANATIONS.CampaignEndsAt}
                </InfoTooltip>
              </div>
            </>
          )}

          {isOfferOfType(selectedTrialOffer, constants.OFFER_TYPE.CrossSell) && (
            <div>
              <SelectInput
                idParam={constants.EXP_IDS.QualifyingProduct}
                label="Qualifying Product"
                options={qualifyingProductOptions}
                value={qualifyingProductOptions.find(x => x.value === selectedTrialOffer?.qualifyingProductId)}
                onChange={(e) => handleOfferChange("qualifyingProductId", e.value)}
                required
                isSearchable
              />
              <InfoTooltip id={`#${constants.EXP_IDS.QualifyingProduct}`}>
                {constants.EXPLANATIONS.QualifyingProduct}
              </InfoTooltip>
            </div>
          )}

          {/* SPR-009 retired — SeatExpansion / Loyalty / Reactivation per-type fields removed from the
              admin picker (types are inert in the engine). Retained for reference:
          {isOfferOfType(selectedTrialOffer, constants.OFFER_TYPE.SeatExpansion) && (
            <div>
              <NumberInput
                label="Minimum Seat Quantity"
                value={selectedTrialOffer?.minSeatQuantity ?? ""}
                onChange={(e) => handleOfferChange("minSeatQuantity", Number.isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber)}
                min={1}
                required
              />
            </div>
          )}

          {isOfferOfType(selectedTrialOffer, constants.OFFER_TYPE.Loyalty) && (
            <div>
              <NumberInput
                label="Minimum Tenure (Days)"
                value={selectedTrialOffer?.minTenureDays ?? ""}
                onChange={(e) => handleOfferChange("minTenureDays", Number.isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber)}
                min={1}
                required
              />
            </div>
          )}

          {isOfferOfType(selectedTrialOffer, constants.OFFER_TYPE.Reactivation) && (
            <div className="text-gray-400 text-xs">
              Reactivation offers gate on the "Days Since Last Offer Threshold" above; no additional field is required.
            </div>
          )}
          */}

          <div className="flex flex-row justify-end gap-x-4 mt-6">
            <div className="w-1/4">
              <PrimaryButton
                text={"Set Offer"}
                onClick={() => {
                  if (!isOfferValid(selectedTrialOffer)) return;
                  let offerToStore = { ...selectedTrialOffer };
                  if (isPercentageOffer(offerToStore)) {
                    offerToStore.price = 0;
                  } else {
                    offerToStore.discountPercent = null;
                  }
                  // EVO-003 / SPR-011 T-003: normalize the application mode. Non-discount-bearing types (Custom)
                  // are always SingleTerm. SingleTerm ignores recurringPeriods (persist null); RecurringPerPeriod
                  // keeps the validated N (>= 2).
                  if (!isDiscountBearingOffer(offerToStore)) {
                    offerToStore.discountMode = constants.DISCOUNT_MODE.SingleTerm.value;
                  }
                  if (!isRecurringOffer(offerToStore)) {
                    offerToStore.recurringPeriods = null;
                  }
                  let productOffers = Array.isArray(product?.trialOffers) ? [...product.trialOffers] : [];
                  if (Number.isInteger(selectedIndex)) {
                    productOffers.splice(selectedIndex, 1, offerToStore);
                  } else {
                    productOffers = [...productOffers, offerToStore];
                  }
                  setProduct({...product, trialOffers: productOffers});
                  closeModal();
                }}
              />
            </div>
            <div className="w-1/4">
              <PrimaryButton
                backgroundColor="gray-300"
                textColor="gray-700"
                text={"Cancel"}
                onClick={closeModal}
              />
            </div>
          </div>
        </div>
      </ModalWrapper>
    </div>
  );
}
